// apps/vendor/src/pages/packing/VendorPackingGroupPage.tsx
//
// Phase 14.1 Wave 8 Task 37 — vendor packing-group own-scope CRUD page.
//
// vg-binding: CONTEXT:decisions=P14.1.D-10
// vg-binding: CONTEXT:decisions=P14.1.D-16
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Per CONTEXT.md P14.1.D-10 (packing-group admin-approval lifecycle) +
// P14.1.D-16 (RBAC: vendor own-scope). The vendor declares co-pack groups
// (POST → status pending), lists ONLY their own groups (GET, TS-11), and edits
// membership/class/name (PATCH — editing an active group → pending_revision,
// old active stays effective). All strings go through i18n (3 locales, packing
// namespace registered below); no fees are shown on this surface so no Money
// formatter is required.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@printway/ui';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { csrfFetch } from '../../lib/api/csrfFetch.js';
import { i18next, useTranslation } from '../../lib/i18n/index.js';
import viPacking from '../../lib/i18n/locales/vi/packing.json';
import enPacking from '../../lib/i18n/locales/en/packing.json';
import frPacking from '../../lib/i18n/locales/fr/packing.json';

// Register the `packing` namespace on the shared i18next instance (this page is
// the only consumer, so a local addResourceBundle avoids editing config.ts).
i18next.addResourceBundle('vi', 'packing', viPacking, true, true);
i18next.addResourceBundle('en', 'packing', enPacking, true, true);
i18next.addResourceBundle('fr', 'packing', frPacking, true, true);

// ---------------------------------------------------------------------------
// Wire types + envelope unwrap (INTERFACE-STANDARDS message priority)
// ---------------------------------------------------------------------------

type PackingGroupStatus =
  | 'pending'
  | 'active'
  | 'pending_revision'
  | 'revoked'
  | 'invalid';

const STATUS_ORDER: readonly PackingGroupStatus[] = [
  'pending',
  'active',
  'pending_revision',
  'revoked',
  'invalid',
];

interface PackingGroupRow {
  id: string;
  name: string;
  location: string;
  packingClass: string;
  status: PackingGroupStatus;
  invalidReason: string | null;
  memberCount: number;
  updatedAt: number;
}

interface ListMeta {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface MemberInput {
  blueprintId: string;
  variantId: string;
}

interface CreateInput {
  name: string;
  location: string;
  packingClass: string;
  members: MemberInput[];
}

interface ReviseInput {
  id: string;
  name?: string;
  packingClass?: string;
  members?: MemberInput[];
}

const PAGE_SIZE = 20;
const PACKING_CLASS_OPTIONS = [
  'apparel-light-A',
  'apparel-heavy-B',
  'fragile-box-C',
  'tube-roll-D',
  'small-rigid-A',
] as const;

class PackingApiError extends Error {
  public readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'PackingApiError';
    this.code = code;
  }
}

interface ApiErrBody {
  ok: false;
  error: { code?: string; message?: string; user_message?: string };
}

async function unwrap<T>(res: Response): Promise<{ data: T; meta?: ListMeta }> {
  const body = (await res.json().catch(() => null)) as
    | { ok: true; data: T; meta?: ListMeta }
    | ApiErrBody
    | null;
  if (!body) {
    throw new PackingApiError('NETWORK', i18next.t('packing:error.network'));
  }
  if (!('ok' in body) || body.ok !== true) {
    const err = (body as ApiErrBody).error;
    // Message priority: user_message → message → generic fallback.
    const message =
      err?.user_message ?? err?.message ?? i18next.t('packing:error.generic');
    throw new PackingApiError(err?.code ?? 'UNKNOWN', message);
  }
  return { data: body.data, meta: body.meta };
}

// ---------------------------------------------------------------------------
// TanStack Query hooks
// ---------------------------------------------------------------------------

const PACKING_KEY = ['vendor', 'packingGroups'] as const;

function usePackingGroups(params: {
  page: number;
  status: PackingGroupStatus | null;
}): UseQueryResult<{ rows: PackingGroupRow[]; meta: ListMeta }, PackingApiError> {
  return useQuery<{ rows: PackingGroupRow[]; meta: ListMeta }, PackingApiError>({
    queryKey: [...PACKING_KEY, params.page, params.status],
    queryFn: async () => {
      const qs = new URLSearchParams();
      qs.set('page', String(params.page));
      qs.set('limit', String(PAGE_SIZE));
      if (params.status) qs.set('status', params.status);
      const res = await csrfFetch(`/api/vendor/packing-groups?${qs.toString()}`, {
        method: 'GET',
      });
      const { data, meta } = await unwrap<PackingGroupRow[]>(res);
      return {
        rows: data,
        meta: meta ?? { page: params.page, limit: PAGE_SIZE, total: data.length, pages: 1 },
      };
    },
  });
}

function useCreatePackingGroup(): UseMutationResult<
  unknown,
  PackingApiError,
  CreateInput
> {
  const qc = useQueryClient();
  return useMutation<unknown, PackingApiError, CreateInput>({
    mutationFn: async (input) => {
      const res = await csrfFetch('/api/vendor/packing-groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      const { data } = await unwrap<unknown>(res);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PACKING_KEY });
    },
  });
}

function useRevisePackingGroup(): UseMutationResult<
  unknown,
  PackingApiError,
  ReviseInput
> {
  const qc = useQueryClient();
  return useMutation<unknown, PackingApiError, ReviseInput>({
    mutationFn: async ({ id, ...patch }) => {
      const res = await csrfFetch(`/api/vendor/packing-groups/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const { data } = await unwrap<unknown>(res);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PACKING_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const HEX_24 = /^[0-9a-fA-F]{24}$/;

/** Parse the members textarea → member inputs (one 24-hex variantId per line). */
function parseMembers(raw: string): MemberInput[] {
  return raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => HEX_24.test(s))
    .map((variantId) => ({ blueprintId: '', variantId }));
}

function statusBadgeClass(status: PackingGroupStatus): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-100 text-emerald-800';
    case 'invalid':
      return 'border border-amber-400 bg-amber-50 text-amber-700';
    case 'revoked':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-pw-surface text-pw-text';
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function VendorPackingGroupPage(): JSX.Element {
  const { t } = useTranslation('packing');
  const [params, setParams] = useSearchParams();

  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const statusFilter = ((): PackingGroupStatus | null => {
    const raw = params.get('status');
    return raw && STATUS_ORDER.includes(raw as PackingGroupStatus)
      ? (raw as PackingGroupStatus)
      : null;
  })();

  const listQuery = usePackingGroups({ page, status: statusFilter });
  const createMut = useCreatePackingGroup();
  const reviseMut = useRevisePackingGroup();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<PackingGroupRow | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Controlled form fields.
  const [fName, setFName] = useState('');
  const [fLocation, setFLocation] = useState('');
  const [fClass, setFClass] = useState<string>(PACKING_CLASS_OPTIONS[0]);
  const [fMembers, setFMembers] = useState('');

  const rows = listQuery.data?.rows ?? [];
  const meta = listQuery.data?.meta;

  // Auto-clear toast after 4s (parity with other vendor pages).
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const setParam = (key: string, value: string | null): void => {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: false });
  };

  const openCreate = (): void => {
    setEditing(null);
    setFName('');
    setFLocation('');
    setFClass(PACKING_CLASS_OPTIONS[0]);
    setFMembers('');
    setFormError(null);
    setSheetOpen(true);
  };

  const openEdit = (row: PackingGroupRow): void => {
    setEditing(row);
    setFName(row.name);
    setFLocation(row.location);
    setFClass(row.packingClass);
    setFMembers('');
    setFormError(null);
    setSheetOpen(true);
  };

  const closeSheet = (): void => {
    setSheetOpen(false);
    setEditing(null);
  };

  const pending = createMut.isPending || reviseMut.isPending;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setFormError(null);
    const members = parseMembers(fMembers);

    if (editing) {
      const patch: ReviseInput = { id: editing.id };
      if (fName.trim() && fName.trim() !== editing.name) patch.name = fName.trim();
      if (fClass && fClass !== editing.packingClass) patch.packingClass = fClass;
      if (members.length > 0) patch.members = members;
      reviseMut.mutate(patch, {
        onSuccess: () => {
          setToast(t('toast.groupUpdated'));
          closeSheet();
        },
        onError: (err) => setFormError(err.message),
      });
      return;
    }

    if (members.length === 0) {
      setFormError(t('error.membersRequired'));
      return;
    }
    createMut.mutate(
      {
        name: fName.trim(),
        location: fLocation.trim(),
        packingClass: fClass,
        members,
      },
      {
        onSuccess: () => {
          setToast(t('toast.groupSubmitted'));
          closeSheet();
        },
        onError: (err) => setFormError(err.message),
      },
    );
  };

  const fromRow = meta && meta.total > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const toRow = meta ? Math.min(page * PAGE_SIZE, meta.total) : 0;

  return (
    <main data-testid="packing-group-page-root" className="flex flex-col gap-4 px-6 py-6">
      <PageHeader
        title={t('page.title')}
        subtitle={t('page.subtitle')}
        actions={
          <button
            type="button"
            data-testid="packing-group-create-btn"
            onClick={openCreate}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-pw-primary px-3.5 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            {t('page.createBtn')}
          </button>
        }
      />

      {/* FilterBar */}
      <section className="flex flex-wrap items-center gap-2" aria-label={t('filter.statusLabel')}>
        <label className="text-sm text-pw-text-muted" htmlFor="pg-status-filter">
          {t('filter.statusLabel')}
        </label>
        <select
          id="pg-status-filter"
          data-testid="packing-group-status-filter"
          value={statusFilter ?? ''}
          onChange={(e) => setParam('status', e.target.value || null)}
          className="rounded-md border border-pw-border bg-pw-bg px-2 py-1.5 text-sm text-pw-text"
        >
          <option value="">{t('filter.statusAll')}</option>
          {STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {t(`status.${s}`)}
            </option>
          ))}
        </select>
      </section>

      {/* Table */}
      <section className="overflow-hidden rounded-lg border border-pw-border bg-pw-bg" aria-label={t('page.title')}>
        <table className="w-full text-sm">
          <thead className="bg-pw-surface text-left text-xs font-medium uppercase tracking-wide text-pw-text-muted">
            <tr>
              <th className="px-4 py-2.5">{t('table.name')}</th>
              <th className="px-4 py-2.5">{t('table.location')}</th>
              <th className="px-4 py-2.5">{t('table.packingClass')}</th>
              <th className="px-4 py-2.5 text-right">{t('table.variantCount')}</th>
              <th className="px-4 py-2.5">{t('table.status')}</th>
              <th className="px-4 py-2.5">{t('table.updated')}</th>
              <th className="px-4 py-2.5 text-right">{t('table.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {listQuery.isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-pw-text-muted">
                  {t('table.loading')}
                </td>
              </tr>
            ) : listQuery.isError ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-red-600">
                  {listQuery.error.message || t('table.loadFailed')}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-pw-text-muted">
                  {t('table.empty')}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} data-testid={`packing-group-row-${row.id}`} className="hover:bg-pw-surface">
                  <td className="px-4 py-3 font-medium text-pw-text">{row.name}</td>
                  <td className="px-4 py-3 text-pw-text-muted">{row.location}</td>
                  <td className="px-4 py-3">
                    <span className="rounded bg-pw-surface px-1.5 py-0.5 font-mono text-xs text-pw-text">
                      {row.packingClass}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{row.memberCount}</td>
                  <td className="px-4 py-3">
                    <span
                      title={row.invalidReason ?? undefined}
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(row.status)}`}
                    >
                      {t(`status.${row.status}`)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-pw-text-muted">
                    {new Date(row.updatedAt).toLocaleString(undefined, {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => openEdit(row)}
                      className="rounded-md border border-pw-border px-2.5 py-1 text-xs font-medium text-pw-text hover:bg-pw-surface"
                    >
                      {t('table.edit')}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {meta && meta.total > 0 ? (
          <footer className="flex items-center justify-between border-t border-pw-border px-4 py-3 text-sm">
            <p className="text-pw-text-muted">
              {t('pagination.summary', { from: fromRow, to: toRow, total: meta.total })}
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setParam('page', String(page - 1))}
                className="rounded-md border border-pw-border px-2.5 py-1.5 text-xs text-pw-text disabled:text-pw-text-muted"
              >
                {t('pagination.prev')}
              </button>
              <button
                type="button"
                disabled={page >= meta.pages}
                onClick={() => setParam('page', String(page + 1))}
                className="rounded-md border border-pw-border px-2.5 py-1.5 text-xs text-pw-text disabled:text-pw-text-muted"
              >
                {t('pagination.next')}
              </button>
            </div>
          </footer>
        ) : null}
      </section>

      {/* Sheet — create / edit */}
      {sheetOpen ? (
        /* dark-ok: modal scrim, translucent black reads correctly in both modes */
        <div className="fixed inset-0 z-40 flex justify-end bg-black/30" role="dialog" aria-modal="true">
          <div className="h-full w-full max-w-lg overflow-y-auto bg-pw-bg shadow-xl">
            <header className="flex items-center justify-between border-b border-pw-border px-5 py-4">
              <div>
                <h2 className="text-base font-semibold text-pw-text">
                  {editing ? t('form.editTitle') : t('form.createTitle')}
                </h2>
                <p className="mt-0.5 text-xs text-pw-text-muted">
                  {editing ? t('form.editHint') : t('form.createHint')}
                </p>
              </div>
              <button
                type="button"
                onClick={closeSheet}
                aria-label={t('form.cancel')}
                className="rounded-md p-1.5 text-pw-text-muted hover:bg-pw-surface"
              >
                ✕
              </button>
            </header>

            <form data-testid="packing-group-form" onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
              <div>
                <label htmlFor="pg-name" className="mb-1 block text-sm font-medium text-pw-text">
                  {t('form.name')}
                </label>
                <input
                  id="pg-name"
                  type="text"
                  required
                  value={fName}
                  onChange={(e) => setFName(e.target.value)}
                  placeholder={t('form.namePlaceholder')}
                  className="w-full rounded-md border border-pw-border px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label htmlFor="pg-location" className="mb-1 block text-sm font-medium text-pw-text">
                  {t('form.location')}
                </label>
                <input
                  id="pg-location"
                  type="text"
                  required={!editing}
                  disabled={editing !== null}
                  value={fLocation}
                  onChange={(e) => setFLocation(e.target.value)}
                  placeholder={t('form.locationPlaceholder')}
                  className="w-full rounded-md border border-pw-border px-3 py-2 font-mono text-sm disabled:bg-pw-surface disabled:text-pw-text-muted"
                />
                <p className="mt-1 text-xs text-pw-text-muted">{t('form.locationHint')}</p>
              </div>

              <div>
                <label htmlFor="pg-class" className="mb-1 block text-sm font-medium text-pw-text">
                  {t('form.packingClass')}
                </label>
                <select
                  id="pg-class"
                  data-testid="packing-group-class-select"
                  value={fClass}
                  onChange={(e) => setFClass(e.target.value)}
                  className="w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm"
                >
                  {PACKING_CLASS_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-pw-text-muted">{t('form.packingClassHint')}</p>
              </div>

              <div>
                <label htmlFor="pg-members" className="mb-1 block text-sm font-medium text-pw-text">
                  {t('form.members')}
                </label>
                <textarea
                  id="pg-members"
                  data-testid="packing-group-members"
                  rows={4}
                  value={fMembers}
                  onChange={(e) => setFMembers(e.target.value)}
                  placeholder={t('form.membersPlaceholder')}
                  className="w-full rounded-md border border-pw-border px-3 py-2 font-mono text-xs"
                />
                <p className="mt-1 text-xs text-pw-text-muted">{t('form.membersHint')}</p>
              </div>

              {formError ? (
                <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {formError}
                </div>
              ) : null}

              <div className="flex items-center justify-end gap-2 border-t border-pw-border pt-4">
                <button
                  type="button"
                  onClick={closeSheet}
                  className="rounded-md px-3.5 py-2 text-sm font-medium text-pw-text-muted hover:bg-pw-surface"
                >
                  {t('form.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={pending}
                  data-testid="packing-group-submit"
                  className="rounded-md bg-pw-primary px-3.5 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                >
                  {editing ? t('form.save') : t('form.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div
          data-testid="packing-group-toast"
          role="status"
          className="fixed bottom-6 right-6 z-50 rounded-md border border-pw-border bg-pw-bg px-4 py-3 text-sm text-pw-text shadow-lg"
        >
          {toast}
        </div>
      ) : null}
    </main>
  );
}

export default VendorPackingGroupPage;
